import { useState } from 'react';
import { BookOpen, PencilLine, Plus } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { QueryError } from '@/components/ui/QueryError';
import { Skeleton } from '@/components/ui/Skeleton';
import { useApiQuery } from '@/hooks/useApiQuery';
import { capitalizeWords } from '@/lib/format';
import type { Subject, SubjectCategory } from '@/types/subject';
import { subjectsApi } from '../api/subjects.api';
import { SUBJECT_CATEGORY_PLURAL } from '../constants';
import { SubjectFormModal } from './SubjectFormModal';

/** What changed, so a form holding subjects by name can follow it. */
export type SubjectChange =
  | { kind: 'added'; subject: Subject }
  | { kind: 'edited'; before: Subject; after: Subject };

export interface ManageSubjectsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The list being managed — the class form opens it from one of its three. */
  category: SubjectCategory;
  onChanged: (change: SubjectChange) => void;
}

/**
 * The school's subject list for one category, managed without leaving the
 * class being set up.
 *
 * The same endpoints, form and rules as the Subjects page — this is a second
 * door to that list, not a second list. A subject is still never typed into a
 * class: it is added here, then picked.
 *
 * Deleting is left to the Subjects page: it takes the subject off every class
 * in the school, too big a step to take from inside one class's form.
 */
export function ManageSubjectsModal({ isOpen, onClose, category, onChanged }: ManageSubjectsModalProps) {
  const plural = SUBJECT_CATEGORY_PLURAL[category];

  const { data, isLoading, error, refetch } = useApiQuery(
    () => (isOpen ? subjectsApi.list({ status: 'active', category }) : Promise.resolve(null)),
    [isOpen, category],
  );
  const subjects = data?.items ?? [];

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState<Subject | null>(null);

  function openForm(subject: Subject | null) {
    setEditing(subject);
    setIsFormOpen(true);
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={`Your ${plural.toLowerCase()}`}
        description="Changes here apply to your whole school. To delete a subject, use the Subjects page."
        size="md"
        footer={
          <>
            <Button type="button" variant="secondary" onClick={onClose}>
              Done
            </Button>
            <Button type="button" leftIcon={<Plus className="h-4 w-4" />} onClick={() => openForm(null)}>
              Add subject
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {error ? (
            <QueryError title={`Could not load your ${plural.toLowerCase()}`} error={error} onRetry={refetch} />
          ) : isLoading ? (
            <div className="flex flex-col gap-2" aria-busy>
              {Array.from({ length: 4 }, (_, index) => (
                <Skeleton key={index} className="h-12 w-full rounded-xl" />
              ))}
            </div>
          ) : subjects.length === 0 ? (
            <EmptyState
              icon={<BookOpen className="h-5 w-5 text-accent" />}
              title={`No ${plural.toLowerCase()} yet`}
              description="Add the first one, then pick it for this class."
            />
          ) : (
            <ul className="flex flex-col gap-2">
              {subjects.map((subject) => {
                const isTaught = subject.usedBy.length > 0;

                return (
                  <li
                    key={subject.uuid}
                    className="flex items-center gap-3 rounded-xl border border-line bg-surface px-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-[560] text-ink capitalize">{subject.name}</p>
                      <p className="truncate text-xs text-ink-3">
                        {isTaught
                          ? `Taught in ${subject.usedBy.length} ${subject.usedBy.length === 1 ? 'class' : 'classes'} this year`
                          : 'No class teaches it this year'}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      iconOnly
                      aria-label={`Edit ${capitalizeWords(subject.name)}`}
                      title={`Edit ${capitalizeWords(subject.name)}`}
                      onClick={() => openForm(subject)}
                    >
                      <PencilLine className="h-3.5 w-3.5" aria-hidden />
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </Modal>

      <SubjectFormModal
        subject={editing}
        defaultCategory={category}
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSaved={(saved) => {
          setIsFormOpen(false);
          onChanged(editing ? { kind: 'edited', before: editing, after: saved } : { kind: 'added', subject: saved });
          refetch();
        }}
      />
    </>
  );
}
